import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AxiosError } from 'axios'
import { api, setAccessToken, setAuthenticationRefresher } from '@/api/client'
import type { ApiEnvelope, CurrentUser, LoginPayload } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const initialized = ref(false)
  let refreshInFlight: Promise<boolean> | null = null
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let accessTokenExpiresAt = 0

  const REFRESH_EARLY_SECONDS = 60
  const TRANSIENT_RETRY_SECONDS = 30

  const authenticated = computed(() => user.value !== null)

  async function login(
    email: string,
    password: string,
    turnstileToken?: string,
  ): Promise<LoginPayload> {
    const response = await api.post<ApiEnvelope<LoginPayload>>('/api/v1/auth/login', {
      email,
      password,
      deviceName: browserDeviceName(),
      turnstileToken,
    })
    if (response.data.data.status === 'AUTHENTICATED') acceptAuthentication(response.data.data)
    initialized.value = true
    return response.data.data
  }

  async function completeMfa(challengeToken: string, code: string): Promise<LoginPayload> {
    const response = await api.post<ApiEnvelope<LoginPayload>>('/api/v1/auth/mfa/complete', {
      challengeToken,
      code,
      deviceName: browserDeviceName(),
    })
    acceptAuthentication(response.data.data)
    initialized.value = true
    return response.data.data
  }

  async function skipMfaEnrollment(challengeToken: string): Promise<LoginPayload> {
    const response = await api.post<ApiEnvelope<LoginPayload>>('/api/v1/auth/mfa/skip', {
      challengeToken,
      deviceName: browserDeviceName(),
    })
    acceptAuthentication(response.data.data)
    initialized.value = true
    return response.data.data
  }

  async function refresh(): Promise<boolean> {
    if (refreshInFlight) {
      return refreshInFlight
    }
    refreshInFlight = (async () => {
      try {
        const response = await api.post<ApiEnvelope<LoginPayload>>('/api/v1/auth/refresh')
        acceptAuthentication(response.data.data)
        return true
      } catch (error) {
        if (isAuthenticationRejected(error)) {
          clearAuthentication()
        } else if (user.value) {
          scheduleRefresh(TRANSIENT_RETRY_SECONDS, false)
        }
        return false
      } finally {
        initialized.value = true
        refreshInFlight = null
      }
    })()
    return refreshInFlight
  }

  async function bootstrap(): Promise<void> {
    if (!initialized.value) {
      await refresh()
    }
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout')
    } finally {
      clearAuthentication()
      initialized.value = true
    }
  }

  async function logoutAll(): Promise<void> {
    try {
      await api.post('/api/v1/auth/logout-all')
    } finally {
      clearAuthentication()
      initialized.value = true
    }
  }

  async function updateLocale(preferredLocale: 'en-US' | 'es-US'): Promise<void> {
    if (!user.value || user.value.preferredLocale === preferredLocale) {
      return
    }
    const response = await api.patch<ApiEnvelope<CurrentUser>>('/api/v1/me', {
      preferredLocale,
      version: user.value.version,
    })
    user.value = response.data.data
  }

  function acceptAuthentication(payload: LoginPayload): void {
    if (!payload.accessToken || !payload.user)
      throw new Error('Authentication response is incomplete')
    setAccessToken(payload.accessToken)
    user.value = payload.user
    scheduleRefresh(payload.expiresIn)
  }

  function clearAuthentication(): void {
    cancelScheduledRefresh()
    accessTokenExpiresAt = 0
    setAccessToken(null)
    user.value = null
  }

  function scheduleRefresh(expiresInSeconds: number, refreshEarly = true): void {
    cancelScheduledRefresh()
    const safeExpiresIn = Number.isFinite(expiresInSeconds) ? Math.max(1, expiresInSeconds) : 900
    accessTokenExpiresAt = Date.now() + safeExpiresIn * 1000
    const delaySeconds = refreshEarly
      ? Math.max(5, safeExpiresIn - REFRESH_EARLY_SECONDS)
      : safeExpiresIn
    refreshTimer = setTimeout(() => {
      void refresh()
    }, delaySeconds * 1000)
  }

  function cancelScheduledRefresh(): void {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  function isAuthenticationRejected(error: unknown): boolean {
    return error instanceof AxiosError && [401, 403].includes(error.response?.status ?? 0)
  }

  function refreshIfNeeded(): void {
    if (user.value && accessTokenExpiresAt > 0 && accessTokenExpiresAt - Date.now() <= 60_000) {
      void refresh()
    }
  }

  function clearLocalAuthentication(): void {
    clearAuthentication()
    initialized.value = true
  }

  function browserDeviceName(): string {
    if (typeof navigator === 'undefined') {
      return 'Web browser'
    }
    return navigator.userAgent.slice(0, 120)
  }

  setAuthenticationRefresher(refresh)
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshIfNeeded()
    })
  }

  return {
    user,
    initialized,
    authenticated,
    login,
    completeMfa,
    skipMfaEnrollment,
    refresh,
    bootstrap,
    logout,
    logoutAll,
    updateLocale,
    clearLocalAuthentication,
  }
})
